// ============================================================================
// The People directory's reads and the person card's writes (item 818).
//
// Reads come from v_people_directory — ONE read-only list over the three
// lists that already exist (employees, workers, service_providers). The view
// is security_invoker, so each row keeps its own list's read rights; this
// hook adds nothing on top.
//
// Writes go back to the record the person lives on, and only to fields that
// already exist there: an hourly worker's phone, email, preferred language and
// status on `workers`. The salaried record is edited on its own card (the
// existing employee card) and a sub on the Subcontractors hub — the person
// card links there rather than carrying a second editor (Ben, 2026-10-06).
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import { commitWrite, useReader } from '../../../shared/save-path';
import type { Database } from '../../../types/database.types';

export type PersonRow = Database['public']['Views']['v_people_directory']['Row'];

export type PersonType = 'salaried' | 'hourly' | '1099' | 'sub_owner';

export const PERSON_TYPE_LABELS: Record<PersonType, string> = {
  salaried: 'Salaried',
  hourly: 'Hourly',
  '1099': '1099 individual',
  sub_owner: 'Sub owner',
};

/** The directory's type filters, in the order Ben named them. */
export type DirectoryFilter = 'everyone' | 'hourly' | 'salaried' | '1099' | 'subs' | 'no_phone' | 'separated';

export const DIRECTORY_FILTERS: ReadonlyArray<{ key: DirectoryFilter; label: string }> = [
  { key: 'everyone', label: 'Everyone' },
  { key: 'hourly', label: 'Hourly' },
  { key: 'salaried', label: 'Salaried' },
  { key: '1099', label: '1099' },
  { key: 'subs', label: 'Subs' },
  { key: 'no_phone', label: 'No phone' },
  { key: 'separated', label: 'Separated' },
];

/** One person = every row sharing a person_group ("hats"). */
export interface Person {
  group: string;
  name: string;
  hats: PersonRow[];
  /** True when any hat is still on a roster. */
  active: boolean;
  hasPhone: boolean;
}

const isActive = (r: PersonRow) => !r.is_separated;

export function groupPeople(rows: readonly PersonRow[]): Person[] {
  const byGroup = new Map<string, PersonRow[]>();
  for (const r of rows) {
    const g = r.person_group ?? r.person_key ?? `${r.source}:${r.record_id}`;
    const list = byGroup.get(g);
    if (list) list.push(r);
    else byGroup.set(g, [r]);
  }
  const people: Person[] = [];
  for (const [group, hats] of byGroup) {
    // The salaried record names the person first, then the hourly one, then the sub.
    const order: Record<string, number> = { employee: 0, worker: 1, provider: 2 };
    hats.sort((a, b) => (order[a.source ?? ''] ?? 9) - (order[b.source ?? ''] ?? 9));
    const name = hats.find((h) => h.display_name && h.display_name.trim())?.display_name?.trim() ?? group;
    people.push({
      group,
      name,
      hats,
      active: hats.some(isActive),
      hasPhone: hats.some((h) => !!h.has_phone),
    });
  }
  people.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return people;
}

const digits = (s: string) => s.replace(/\D/g, '');

/**
 * The one search box: a name or a phone. A phone matches on digits so
 * "(816) 555" finds 8165550123; a name matches any hat's name or company.
 */
export function matchesSearch(p: Person, q: string): boolean {
  const query = q.trim().toLowerCase();
  if (!query) return true;
  const qDigits = digits(query);
  if (qDigits.length >= 3 && qDigits.length === query.replace(/[\s()+\-.]/g, '').length) {
    return p.hats.some((h) => h.phone && digits(h.phone).includes(qDigits));
  }
  return p.hats.some(
    (h) =>
      (h.display_name ?? '').toLowerCase().includes(query) ||
      (h.company_name ?? '').toLowerCase().includes(query) ||
      (h.record_id ?? '').toLowerCase() === query,
  );
}

/**
 * The type filters. "No phone" is the PIN gap: an active hourly worker with no
 * mobile number — the same predicate the Desk's Needs-info row counts and the
 * one the live count in the report is measured with.
 */
export function matchesFilter(p: Person, f: DirectoryFilter): boolean {
  switch (f) {
    case 'everyone':
      return p.active;
    case 'hourly':
      return p.hats.some((h) => h.person_type === 'hourly' && isActive(h));
    case 'salaried':
      return p.hats.some((h) => h.person_type === 'salaried' && isActive(h));
    case '1099':
      return p.hats.some((h) => h.person_type === '1099' && isActive(h));
    case 'subs':
      return p.hats.some((h) => h.person_type === 'sub_owner' && isActive(h));
    case 'no_phone':
      return p.hats.some(isNoPhoneWorker);
    case 'separated':
      return !p.active;
  }
}

/** The PIN gap, one row at a time: an active hourly worker with no mobile number. */
export function isNoPhoneWorker(h: PersonRow): boolean {
  return h.person_type === 'hourly' && h.status === 'active' && !h.has_phone;
}

export function usePeopleDirectory() {
  const [rows, setRows] = useState<PersonRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('v_people_directory')
      .select('*')
      .order('display_name', { ascending: true });
    if (err) {
      setError(errorMessage(err, 'Could not load the directory.'));
      setRows([]);
    } else {
      setRows(data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useReader(['workers', 'employees', 'service_providers'], load);

  const people = useMemo(() => groupPeople(rows), [rows]);
  return { rows, people, loading, error, refetch: load };
}

export function usePerson(group: string | undefined) {
  const [hats, setHats] = useState<PersonRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!group) {
      setHats([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('v_people_directory')
      .select('*')
      .eq('person_group', group);
    if (err) {
      setError(errorMessage(err, 'Could not load this person.'));
      setHats([]);
    } else {
      setHats(data ?? []);
    }
    setLoading(false);
  }, [group]);

  useEffect(() => {
    void load();
  }, [load]);
  useReader(['workers', 'employees', 'service_providers', 'worker_credentials'], load);

  const person = useMemo(() => (hats.length ? groupPeople(hats)[0] : null), [hats]);
  return { person, loading, error, refetch: load };
}

/** The fields the card edits on an hourly worker — all already on `workers`. */
export interface WorkerContactPatch {
  phone_mobile?: string | null;
  email?: string | null;
  preferred_language?: string;
  worker_status?: 'active' | 'inactive' | 'terminated';
  /** Works for, and the type that must agree with it (workers_crew_has_employer). */
  employer_provider_id?: string | null;
  employment_class?: string;
}

export async function updateWorkerContact(workerId: string, patch: WorkerContactPatch): Promise<void> {
  await commitWrite({
    label: 'save this worker',
    write: supabase.from('workers').update(patch).eq('worker_id', workerId).select('worker_id'),
    touches: ['workers'],
  });
}

/**
 * Whether a worker has a clock-in code on record (the hash itself is never read).
 * Honest since decision 108 (item #822): v_time_worker_picker.has_pin reads a
 * definer function, so an app user sees the truth.
 */
export async function workerHasPin(workerId: string): Promise<{ set: boolean } | null> {
  const { data, error } = await supabase
    .from('v_time_worker_picker')
    .select('worker_id, has_pin')
    .eq('worker_id', workerId)
    .maybeSingle();
  if (error || !data) return null;
  return { set: !!(data as { has_pin?: boolean | null }).has_pin };
}
